import { createContext, useState, useContext, useEffect, useCallback } from 'react';
import ApiService from '../services/api';
import { AuthContext } from './AuthContext';

export const FavouriteContext = createContext(null);

export const FavouriteContextProvider = ({ children }) => {
  const { isLoggedIn, loading: authLoading } = useContext(AuthContext);
  const [favourites, setFavourites] = useState({
    book: new Set(),
    writer: new Set(),
    publication: new Set(),
    category: new Set(),
  });
  const [loading, setLoading] = useState(false);

  const fetchFavourites = useCallback(async () => {
    setLoading(true);
    try {
      const response = await ApiService.getFavourites();
      
      const newFavourites = {
        book: new Set(),
        writer: new Set(),
        publication: new Set(),
        category: new Set(),
      };

      response.results.forEach(fav => {
        if (newFavourites[fav.favourite_type]) {
          newFavourites[fav.favourite_type].add(fav.object_id);
        }
      });

      setFavourites(newFavourites);
    } catch (error) {
      console.error('Failed to fetch favourites:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch favourites when user logs in
  useEffect(() => {
  if (!authLoading) {
    if (isLoggedIn) {
      fetchFavourites();
    } else {
      setFavourites({
        book: new Set(),
        writer: new Set(),
        publication: new Set(),
        category: new Set(),
      });
    }
  }
}, [isLoggedIn, authLoading, fetchFavourites]);

  const toggleFavourite = useCallback(async (type, objectId) => {
    if (!isLoggedIn) {
      throw new Error('Not logged in');
    }

    const isCurrentlyFavourited = favourites[type].has(objectId);

    setFavourites(prev => {
      const newSet = new Set(prev[type]);
      if (isCurrentlyFavourited) {
        newSet.delete(objectId);
      } else {
        newSet.add(objectId);
      }
      return { ...prev, [type]: newSet };
    });

    try {
      await ApiService.toggleFavourite(type, objectId);
    } catch (error) {
      // Revert on error
      setFavourites(prev => {
        const newSet = new Set(prev[type]);
        if (isCurrentlyFavourited) {
          newSet.add(objectId);
        } else {
          newSet.delete(objectId);
        }
        return { ...prev, [type]: newSet };
      });
      throw error;
    }
  }, [favourites, isLoggedIn]);

  const isFavourite = useCallback((type, objectId) => {
    return favourites[type]?.has(objectId) || false;
  }, [favourites]);

  return (
    <FavouriteContext.Provider
      value={{
        favourites,
        loading,
        toggleFavourite,
        isFavourite,
        refetch: fetchFavourites,
      }}
    >
      {children}
    </FavouriteContext.Provider>
  );
};

export const useFavourite = () => {
  const context = useContext(FavouriteContext);
  if (!context) {
    throw new Error('useFavourite must be used within FavouriteContextProvider');
  }
  return context;
};

export default FavouriteContext;
